import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type Ref
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled, ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import sanitizeDrupalHtml from '../util/sanitizeHTML';
import { pickStableSection, reanchorScrollDelta } from './scrollSpy';
import './Microsite.scss';

export function resolveLogoName(logo: any, fallback: string): string {
  const candidate = (
    logo?.name ||
    logo?.title ||
    logo?.image?.alt ||
    logo?.alt ||
    logo?.id ||
    ''
  ).trim();
  return candidate || fallback;
}

const firstDrupalItem = (value: any) => (Array.isArray(value) ? value[0] ?? {} : {});

const getFullImageLink = (path?: string) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  const env = process.env.REACT_APP_ENV_LOADED;
  let base = 'https://content-ndc-portal.aircanada.com';
  if (env === 'DEV' || env === 'INT' || env === 'CRT') {
    base = 'https://content-ndc-portal-int.aircanada.com';
  } else if (env === 'BAT' || env === 'PREPROD') {
    base = 'https://content-ndc-portal-bat.aircanada.com';
  }

  return `${base}${path}`;
};

export const microSiteTheme = createTheme({
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#000',
        }
      }
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1440,
      xl: 2560
    }
  }
});

const MAX_WIDTH = 1200;
const NAV_DESKTOP_HEIGHT = 64;
const NAV_MOBILE_HEIGHT = 56;
const SECTION_TOP_GAP = 16;
const AT_BOTTOM_TOLERANCE_PX = 4;
const USER_SCROLL_IDLE_MS = 150;
const USER_SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' '
]);
const IDS = [
  'overview',
  'about',
  'agenda',
  'locations',
  'participants'
] as const;
type SectionId = (typeof IDS)[number];
type AnchorId = Exclude<SectionId, 'overview'>;

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: MAX_WIDTH,
  marginInline: 'auto',
  [theme.breakpoints.down('lg')]: { paddingInline: theme.spacing(4) },
}));

const Section = styled('section', {
  shouldForwardProp: (prop) => prop !== 'shaded' && prop !== 'scrollOffset'
})<{
  shaded?: boolean;
  scrollOffset: number;
}>(({ theme, shaded, scrollOffset }) => ({
  width: '100%',
  scrollMarginTop: `${scrollOffset}px`,
  paddingBlock: theme.spacing(8),
  background: shaded ? '#f7f7f7' : theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    paddingBlock: theme.spacing(6)
  },
  [theme.breakpoints.down('sm')]: {
    paddingBlock: theme.spacing(4)
  }
}));

const SectionContent = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: { gap: theme.spacing(2.25) }
}));

// Drupal-HTML heading wrapper styled via Microsite.scss (.microsite-heading)
const Heading = 'div' as const;
const CardTitle = 'div' as const;

const Text = styled(Typography)(({ theme }) => ({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 14,
  lineHeight: 1.5,
  [theme.breakpoints.up('sm')]: { fontSize: 16 },
  '& p': { margin: 0, marginBottom: theme.spacing(2) },
  '& p:last-child': { marginBottom: 0 },
  '& a': { color: '#1460aa', fontWeight: 700 },
  '& ul, & ol': { paddingInlineStart: theme.spacing(3) }
})) as typeof Typography;

const Action = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'flag'
})<{ flag?: number | string }>(({ theme, flag = 0 }) => ({
  alignSelf: flag === 1 ? 'center' : 'start',
  padding: '12px 32px',
  border: 'none',
  borderRadius: 25,
  backgroundColor: '#1460AA',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: 'none',
  transition:
    'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  [theme.breakpoints.between('sm', 'md')]: {
    padding: flag === 1 ? '2px': '12px 32px',
    minWidth: '80px !important'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '5px 16px',
    fontSize: '14px',
    width: flag === 2 ? '100%' : 'unset'
  },

  '&:hover': {
    backgroundColor: '#003d78',
    borderColor: '#003d78',
    color: '#ffffff',
    textDecoration: 'none'
  },
  '&:focus-visible': {
    outline: '2px solid #000',
    outlineOffset: 2
  }
}));

const OutlineAction = styled((props) => <Action {...props} flag={2} />)({
  color: '#1460aa',
  flag: 2,
  border: '1px solid #1460aa',
  background: '#fff',
  '&:hover': { color: '#0f4f8a', borderColor: '#0f4f8a', background: '#f1f7fc' }
}) as typeof Action;

const CoverImage = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});
const SmallTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 20,
  fontWeight: 700,
  flex: 1,
  lineHeight: '28px',
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    lineHeight: '24px',
    alignSelf: 'center'
  }
})) as typeof Typography;
const SmallText = styled(Typography)({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 13,
  lineHeight: 1.45
}) as typeof Typography;
const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'start'
  } 
}));

const AccordionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));
const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
});

const SkipToContentLink = styled('a')(({ theme }) => ({
  position: 'fixed',
  left: -9999,
  top: 0,
  zIndex: 9999,
  padding: '10px 16px',
  background: '#1460aa',
  color: '#fff',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  borderRadius: 4,
  textDecoration: 'none',
  '&:focus, &:focus-visible': {
    left: 16,
    top: 16,
    outline: '2px solid #000',
    outlineOffset: 2
  }
}));

const NavSpacer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: NAV_MOBILE_HEIGHT,

  [theme.breakpoints.up('sm')]: {
    height: NAV_DESKTOP_HEIGHT
  }
}));

/* Sticky white navigation */
const StickyNav = styled('nav', {
  shouldForwardProp: (prop) => prop !== 'topPosition'
})<{ topPosition?: number | string }>(({ theme, topPosition = 0 }) => ({
  position: 'fixed',
  top: typeof topPosition === 'number' ? `${topPosition}px` : topPosition,
  zIndex: 2,
  width: '100%',
  backgroundColor: '#fff'
}));

const DesktopNav = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  maxWidth: 'unset',
  paddingInline: '2rem',
  minHeight: NAV_DESKTOP_HEIGHT,
  [theme.breakpoints.down('sm')]: { display: 'none' }
}));

const DesktopTabs = styled(Box)({
  display: 'flex',
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' }
});
const DesktopTab = styled('button', {
  shouldForwardProp: (p) => p !== 'active'
})<{ active: boolean }>(({ theme, active }) => ({
  position: 'relative',
  flexShrink: 0,
  minHeight: NAV_DESKTOP_HEIGHT,
  padding: theme.spacing(1.5, 2.25),
  border: 0,
  color: active ? '#1460aa' : '#161616',
  background: 'transparent',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 16,
  fontWeight: active ? 700 : 600,
  cursor: 'pointer',
  '&:hover': { color: '#1460aa', background: '#f6f9fc' },
  '&:focus-visible': {
    zIndex: 1,
    outline: '2px solid #000',
    outlineOffset: '-3px'
  }
}));
const ActiveLine = styled('span')({
  position: 'absolute',
  right: 8,
  bottom: 0,
  left: 8,
  height: 4,
  background: '#1460aa'
});
const NavCta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  paddingLeft: theme.spacing(2),
  '& .MuiButton-root': { minWidth: 106 }
}));

const MobileNav = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: { display: 'block' }
}));
const MobileBar = styled(Container)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: NAV_MOBILE_HEIGHT
});
const MenuButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 86,
  minHeight: 44,
  padding: theme.spacing(1),
  border: 0,
  color: '#111',
  background: 'transparent',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  '&:focus-visible': { outline: '2px solid #000', outlineOffset: '-2px' }
}));
const MobileMenuPanel = styled(Paper)({
  position: 'absolute',
  top: '100%',
  right: 0,
  left: 0,
  borderRadius: 0,
  background: '#fff',
  boxShadow: '0 8px 16px rgba(0,0,0,.16)'
});
const MobileMenuList = styled('ul')({
  maxWidth: MAX_WIDTH,
  margin: '0 auto',
  padding: '0 16px 12px',
  listStyle: 'none'
});
const MobileMenuItem = styled('button', {
  shouldForwardProp: (p) => p !== 'active'
})<{ active: boolean }>(({ active }) => ({
  position: 'relative',
  display: 'block',
  width: '100%',
  minHeight: 44,
  padding: '10px 8px',
  border: 0,
  color: active ? '#1460aa' : '#111',
  background: '#fff',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 14,
  fontWeight: active ? 700 : 500,
  textAlign: 'left',
  cursor: 'pointer',
  '&:focus-visible': { outline: '2px solid #000', outlineOffset: -2 },
  ...(active
    ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: -16,
          width: 4,
          background: '#1460aa'
        }
      }
    : {})
}));

/* Overview */
const Hero = styled(Section)(({ theme }) => ({
  background: '#f7f7f7',
  [theme.breakpoints.down('sm')]: { paddingTop: theme.spacing(8) }
}));
const HeroGrid = styled(Container)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'minmax(0,1fr) minmax(280px,1fr)',
    alignItems: 'center',
    gap: theme.spacing(4)
  }
}));
const HeroCopy = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: { alignItems: 'stretch', textAlign: 'center' }
}));
// Drupal-HTML hero title wrapper styled via Microsite.scss (.microsite-hero-title)
// using plain className + descendant selectors, matching AppRoadMap's pattern.
const HeroTitle = 'div' as const;
const HeroInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#000',
  gap: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
    fontSize: '18px',
    lineHeight: '24px'
  }
}));
const HeroImage = styled(Box)(({ theme }) => ({
  borderRadius: '1rem'
}));

/* Copy section link */
const AnchorHeadingRow = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: { alignSelf: 'center' }
}));
const LinkButton = styled('button', {
  shouldForwardProp: (p) => p !== 'visible'
})<{ visible: boolean }>(({ theme, visible }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: 0,
  border: 0,
  borderRadius: 4,
  color: '#1460aa',
  background: 'transparent',
  cursor: 'pointer',
  opacity: visible ? 1 : 0,
  visibility: visible ? 'visible' : 'hidden',
  pointerEvents: visible ? 'auto' : 'none',
  [theme.breakpoints.up('md')]: {
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto'
  },
  '&:focus-visible': { outline: '2px solid #000', outlineOffset: 2 }
}));
const CopyPopper = styled(Popper)(({ theme }) => ({
  zIndex: 1
}));
const CopySurface = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minWidth: 220,
  padding: theme.spacing(2, 3),
  borderRadius: '16px',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 700,
  textAlign: 'center',
  boxShadow: '0 10px 28px rgba(0,0,0,.15)',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    width: 16,
    height: 16,
    background: '#fff',
    transform: 'translateX(-50%) rotate(45deg)'
  }
}));

/* Cards, accordions, locations */
const Grid3 = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3,minmax(0,1fr))'
  }
}));
const Card = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  height: '100%',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0,0,0,.12)'
}));

const Card2 = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'start',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  height: '100%',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0,0,0,.12)',
}));
const Badge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 22,
  padding: '2px 10px',
  borderRadius: 99,
  color: '#fff',
  background: '#000',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap'
});
const AccordionList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));
const AccordionShell = styled(Paper)(({ theme }) => ({
  overflow: 'hidden',
  borderRadius: 16,
  background: '#fff',
  boxShadow: ' 0 0 15px 0 rgba(0, 0, 0, 0.15)'
}));
const AccordionTrigger = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  width: '100%',
  border: 0,
  color: '#111',
  background: '#fff',
  textAlign: 'left',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: '2px solid #000',
    outlineOffset: '-3px',
    borderRadius: '1rem'
  }
}));
const Chevron = styled('span', { shouldForwardProp: (p) => p !== 'open' })<{
  open: boolean;
}>(({ open }) => ({
  transform: !open ? 'rotate(180deg)' : 'none',
  transition: 'transform .18s'
}));

const AccordionPanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: theme.spacing(3),
    right: theme.spacing(3),
    borderTop: '1px solid #dedede'
  }
}));

const Html = styled(Box)(({ theme }) => ({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 13,
  lineHeight: 1.5,
  '& p': { margin: 0, marginBottom: theme.spacing(0.75) },
  '& p:last-child': { marginBottom: 0 },
  '& strong': { color: '#1460aa', fontWeight: 700 }
}));
const VenueGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2,minmax(0,1fr))'
  }
}));
const VenueCard = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25)
}));
const VenueImageFrame = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  aspectRatio: '16/10',
  borderRadius: 12,
  background: '#eee'
});
const VenueOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 0,
  padding: theme.spacing(3),
  color: '#fff',
  background: 'rgba(0, 0, 0, 0.60)',
  backdropFilter: 'blur(8px)',
  [theme.breakpoints.down('md')]: {
    backdropFilter: 'unset',
    background: '#000'
  }
}));

const OverlayTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: '20px',
  color: '#fff',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px'
  }
}));

const OverlayAddress = styled(Typography)(({ theme }) => ({
  marginTop: 3,
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 12,
  lineHeight: '16px',
  color: '#fff'
})) as typeof Typography;

const Directions = styled('a')(({ theme }) => ({
  alignSelf: 'flex-start',
  color: '#1460aa',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': { outline: '2px solid #000', outlineOffset: 3 },
  [theme.breakpoints.down('md')]: {
    alignSelf: 'center'
  }
}));
const ShuttleGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0,1fr) minmax(360px,.9fr)'
  },
  [theme.breakpoints.down('md')]: {
    '& > :first-child': {
      order: 2
    },
    '& > :last-child': {
      order: 1
    }
  }
}));
const ShuttleImage = styled(VenueImageFrame)(({ theme }) => ({
  aspectRatio: '16/8',
  [theme.breakpoints.up('lg')]: { aspectRatio: '4/3' }
}));
const LogoGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
  gap: theme.spacing(3, 3),
  alignItems: 'center',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3,minmax(0,1fr))'
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4,minmax(0,1fr))'
  }
}));
const LogoItem = styled('li')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});
const Logo = styled('img')({
  display: 'block',
  width: '100%',
  margin: 'auto',
  objectFit: 'contain'
});

function Register({ data, flag }: { data: any; flag?: number }) {
  const handleClick = () => {
    if (!data?.url) return;

    window.open(data.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Action flag={flag} onClick={handleClick}>
      <span dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(data?.label) }} />
    </Action>
  );
}

function CustomAccordion({
  summary,
  children,
  open,
  onChange,
  label
}: {
  summary: ReactNode;
  children: ReactNode;
  open: boolean;
  onChange: (open: boolean) => void;
  label: string;
}) {
  const id = useId().replace(/:/g, '');
  return (
    <AccordionShell elevation={0}>
      <Typography component="h3">
        <AccordionTrigger
          id={`a-${id}`}
          type="button"
          aria-expanded={open}
          aria-label={label || undefined}
          sx={{ padding: '24px' }}
          aria-controls={`p-${id}`}
          onClick={() => onChange(!open)}
        >
          <Box aria-hidden="true">{summary}</Box>
          <Chevron open={open} aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="9"
              viewBox="0 0 15 9"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M-1.64477e-07 7.49369L7.49369 -3.28954e-07L14.9874 7.49369L13.7033 8.77778L7.49369 2.56818L1.28409 8.77778L-1.64477e-07 7.49369Z"
                fill="#1460AA"
              />
            </svg>
          </Chevron>
        </AccordionTrigger>
      </Typography>
      <Collapse in={open} unmountOnExit>
        <AccordionPanel
          id={`p-${id}`}
          role="region"
          aria-labelledby={`a-${id}`}
        >
          {children}
        </AccordionPanel>
      </Collapse>
    </AccordionShell>
  );
}

const AgendaItems = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(1.75)
  }
}));

const AgendaItemRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '105px minmax(0, 1fr)',
  columnGap: theme.spacing(1.5),
  alignItems: 'start',
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '105px minmax(0, 1fr)',
    columnGap: theme.spacing(2)
  },

  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '105px minmax(0, 1fr)'
  }
}));

const AgendaTime = styled(Typography)(({ theme }) => ({
  color: '#1460AA',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '22px',
  whiteSpace: 'nowrap',

  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    lineHeight: '20px'
  }
}));

const SmallTextH4 = styled(Typography)(({ theme }) => ({
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '22px',
  whiteSpace: 'nowrap',

  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    lineHeight: '20px'
  }
})) as typeof Typography;

const AgendaItemContent = styled(Box)({
  minWidth: 0
});

const AgendaItemHtml = styled(Box)(({ theme }) => ({
  minWidth: 0,
  color: '#111111',
  fontFamily: "'AC Nord Text', sans-serif",
  fontSize: 13,
  lineHeight: '22px',

  /*
   * First paragraph is the agenda title.
   */
  '& p:first-of-type': {
    margin: 0,
    color: '#000',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '22px',

    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      lineHeight: '20px'
    }
  },

  /*
   * Remaining paragraphs are subtitles/descriptions.
   */
  '& p:not(:first-of-type)': {
    margin: 0,
    marginTop: theme.spacing(0.25),
    color: '#6D6D6F',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',

    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      lineHeight: '16px'
    }
  },

  '& ul, & ol': {
    margin: theme.spacing(0.75, 0, 0),
    paddingInlineStart: theme.spacing(2.5),
    color: '#6D6D6F',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',

    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      lineHeight: '16px'
    }
  },

  '& li + li': {
    marginTop: theme.spacing(0.25)
  },

  '& strong': {
    fontWeight: 700
  },

  '& a': {
    color: '#1460AA',
    fontWeight: 700,
    textDecoration: 'underline'
  }
}));

function AnchorHeading({
  id,
  label
}: {
  id: AnchorId;
  label: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const ref = useRef<HTMLButtonElement | null>(null);
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState<boolean>(false);
  const copy = async () => {
    const url = new URL(window.location.href);
    url.hash = id;
    try {
      await navigator.clipboard.writeText(url.toString());
    } catch {
      const el = document.createElement('textarea');
      el.value = url.toString();
      document.body.appendChild(el);
      el.select();
      // `execCommand` is deprecated with no drop-in replacement for synchronous,
      // cross-browser copy fallback; cast to bypass the deprecated typed overload.
      (document as Document & { execCommand: (commandId: string) => boolean }).execCommand('copy');
      el.remove();
    }
    window.history.replaceState({ section: id }, '', `#${id}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <AnchorHeadingRow>
      <Heading
        id={`${id}-title`}
        className="microsite-heading"
        tabIndex={-1}
        dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(label) }}
      />
      <LinkButton
        ref={ref}
        type="button"
        visible={true}
        aria-label={`${t('accessibilityMicrosite.copySectionLink')}: ${label?.replace(/<[^>]*>/g, '')}`}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          void copy();
        }}
        onMouseEnter={() => desktop ? setHover(true) : undefined }
        onMouseLeave={() => desktop ? setHover(false) : undefined }
        onFocus={() => desktop ? setHover(true) : undefined}
        onBlur={() => desktop ? setHover(false) : undefined}
      >
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
        >
          <path
            d="M14.6666 22.6667H9.33329C7.48885 22.6667 5.9164 22.0165 4.61596 20.716C3.31552 19.4156 2.66574 17.8436 2.66663 16C2.66663 14.1556 3.31685 12.5832 4.61729 11.2827C5.91774 9.98226 7.48974 9.33249 9.33329 9.33337H14.6666V12H9.33329C8.22218 12 7.27774 12.3889 6.49996 13.1667C5.72218 13.9445 5.33329 14.8889 5.33329 16C5.33329 17.1112 5.72218 18.0556 6.49996 18.8334C7.27774 19.6112 8.22218 20 9.33329 20H14.6666V22.6667ZM10.6666 17.3334V14.6667H21.3333V17.3334H10.6666ZM17.3333 22.6667V20H22.6666C23.7777 20 24.7222 19.6112 25.5 18.8334C26.2777 18.0556 26.6666 17.1112 26.6666 16C26.6666 14.8889 26.2777 13.9445 25.5 13.1667C24.7222 12.3889 23.7777 12 22.6666 12H17.3333V9.33337H22.6666C24.5111 9.33337 26.0835 9.9836 27.384 11.284C28.6844 12.5845 29.3342 14.1565 29.3333 16C29.3333 17.8445 28.6831 19.4169 27.3826 20.7174C26.0822 22.0178 24.5102 22.6676 22.6666 22.6667H17.3333Z"
            fill="#1460AA"
          />
        </svg>
      </LinkButton>
      <CopyPopper
        open={((hover && desktop) || copied)}
        anchorEl={ref.current}
        placement="top"
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
      >
        <CopySurface role="status" aria-live="polite">
          {copied
            ? t('accessibilityMicrosite.sectionLinkCopied')
            : !copied && desktop
            ? t('accessibilityMicrosite.copySectionLink')
            : null}
        </CopySurface>
      </CopyPopper>
    </AnchorHeadingRow>
  );
}
function SectionNav({
  active,
  go,
  registration,
  logoHeight,
  navRef
}: {
  active: SectionId;
  go: (id: SectionId) => void;
  registration: any;
  logoHeight: number;
  navRef?: Ref<HTMLElement>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  const raw = t('microsite_tabs', {
    returnObjects: true,
    defaultValue: []
  }) as Array<{ id: number; value: string }>;

  const tabs = IDS.map((id, index) => ({
    value: id,
    label: raw[index]?.value
  })).filter((tab) => Boolean(tab.label));

  const closeMobileMenu = () => {
    setOpen(false);
    menuButtonRef.current?.focus();
  };

  const choose = (id: SectionId) => {
    setOpen(false);
    go(id);
  };

  return (
    <>
      <StickyNav
        ref={navRef}
        topPosition={logoHeight}
        aria-label={t('accessibilityMicrosite.sectionNavigationLabel')}
      >
        <DesktopNav>
          <DesktopTabs>
            {tabs.map((tab) => {
              const id = tab.value as SectionId;
              const isActive = active === id;

              return (
                <DesktopTab
                  key={id}
                  active={isActive}
                  aria-current={isActive ? 'location' : undefined}
                  onClick={() => choose(id)}
                >
                  <span dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(tab.label) }} />

                  {isActive && <ActiveLine aria-hidden="true" />}
                </DesktopTab>
              );
            })}
          </DesktopTabs>

          {active !== 'overview' && (
            <NavCta>
              <Register flag={1} data={registration} />
            </NavCta>
          )}
        </DesktopNav>

        <MobileNav>
          <MobileBar>
            <MenuButton
              ref={menuButtonRef}
              type="button"
              aria-expanded={open}
              aria-controls="mobile-section-menu"
              onClick={() => setOpen((current) => !current)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && open) {
                  e.preventDefault();
                  closeMobileMenu();
                }
              }}
            >
              {open ? (
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                    fill="black"
                  />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12.9999H5V10.9999H3V12.9999ZM3 16.9999H5V14.9999H3V16.9999ZM3 8.99988H5V6.99988H3V8.99988ZM7 12.9999H21V10.9999H7V12.9999ZM7 16.9999H21V14.9999H7V16.9999ZM7 6.99988V8.99988H21V6.99988H7Z"
                    fill="black"
                  />
                </svg>
              )}

              {t('microSite.navigation.menu')}
            </MenuButton>

            {active !== 'overview' && <Register flag={1} data={registration} />}
          </MobileBar>

          <Collapse in={open} timeout="auto">
            <MobileMenuPanel
              id="mobile-section-menu"
              elevation={0}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  closeMobileMenu();
                }
              }}
            >
              <MobileMenuList>
                {tabs.map((tab) => {
                  const id = tab.value as SectionId;
                  const isActive = active === id;

                  return (
                    <li key={id}>
                      <MobileMenuItem
                        type="button"
                        active={isActive}
                        aria-current={isActive ? 'location' : undefined}
                        onClick={() => choose(id)}
                        aria-label={tab.label}
                      >
                        <span dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(tab.label) }} />
                      </MobileMenuItem>
                    </li>
                  );
                })}
              </MobileMenuList>
            </MobileMenuPanel>
          </Collapse>
        </MobileNav>
      </StickyNav>

      <NavSpacer aria-hidden="true" />
    </>
  );
}

function Overview({ scrollOffset }: { scrollOffset: number }) {
  const { t } = useTranslation();

  const hero = firstDrupalItem(t('microsite_herosection', {
    returnObjects: true,
    defaultValue: []
  }));
  const data = {
    title: hero.title,
    dates: hero.date,
    venue: hero.location,
    register: {
      label: hero.registerUrl?.title,
      url: hero.registerUrl?.uri
    },
    image: hero.image
  };

  return (
    <Hero
      id="overview"
      scrollOffset={scrollOffset}
      aria-labelledby="overview-title"
    >
      <HeroGrid>
        <HeroCopy>
          <HeroTitle
            id="overview-title"
            className="microsite-hero-title"
            tabIndex={-1}
            dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(data.title) }}
          />

          <HeroInfo>
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8 2V4H16V2H18V4H19C20.1 4 21 4.9 21 6V20C21 21.1 20.1 22 19 22H5C3.89 22 3 21.1 3 20L3.00977 6C3.00977 4.9 3.89 4 5 4H6V2H8ZM5 9V20H19V9H5ZM9 13H7V11H9V13ZM13 13H11V11H13V13ZM17 13H15V11H17V13Z"
                fill="#1460AA"
              />
            </svg>
            <SmallText dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(data.dates) }} />
          </HeroInfo>

          <HeroInfo>
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M5 9C5 5.13 8.13 2 12 2C15.87 2 19 5.13 19 9C19 14.25 12 22 12 22C12 22 5 14.25 5 9ZM9.5 9C9.5 10.38 10.62 11.5 12 11.5C13.38 11.5 14.5 10.38 14.5 9C14.5 7.62 13.38 6.5 12 6.5C10.62 6.5 9.5 7.62 9.5 9Z"
                fill="#1460AA"
              />
            </svg>
            <SmallText dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(data.venue) }} />
          </HeroInfo>

          <Register flag={2} data={data.register} />
        </HeroCopy>

        <HeroImage>
          <CoverImage
            src={getFullImageLink(data.image?.url)}
            alt={data.image?.alt}
          />
        </HeroImage>
      </HeroGrid>
    </Hero>
  );
}

function About({
  goLocations,
  scrollOffset
}: {
  goLocations: () => void;
  scrollOffset: number;
}) {
  const { t } = useTranslation();
  const about = firstDrupalItem(t('microsite_about', {
    returnObjects: true,
    defaultValue: []
  }));
  const eventDetails = about.eventDeatils?.[0] ?? {};
  const d = {
    heading: about.heading,
    body: about.description,
    highlights: about.highlights?.map((item: any) => ({
      id: item.id,
      title: item.title,
      body: item.description,
      image: {
        alt: item.image?.alt,
        value: getFullImageLink(item.image?.url)
      }
    })),
    body2: about.conclusionNote,
    eventDetails: {
      heading: eventDetails.heading,
      seeLocationDetails: eventDetails.ctaLink?.title ?? eventDetails.cta,
      days: eventDetails.days?.map((day: any) => ({
        id: day.id,
        badge: day.badge,
        date: day.date,
        body: day.description
      }))
    }
  };
  return (
    <Section
      scrollOffset={scrollOffset}
      id="about"
      aria-labelledby="about-title"
    >
      <SectionContent>
        <AnchorHeading
          id="about"
          label={d.heading}
        />
        <Text
          component="div"
          dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.body) ?? '' }}
        />
        <Grid3>
          {d.highlights?.map((x: any, i: number) => (
            <Card key={x.title ?? i} elevation={0}>
              <Box
                component="img"
                src={x.image?.value}
                alt={x.image?.alt ?? ''}
                width={40}
                height={36}
              />
              <CardTitle
                className="microsite-card-title"
                dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(x.title) }}
              />
              <Html sx={{textAlign: 'center'}} dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(x.body) ?? '' }} />
            </Card>
          ))}
        </Grid3>
        {d.body2 && (
          <Text component="div" dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.body2) }} />
        )}
        <CardTitle
          className="microsite-card-title"
          dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.eventDetails?.heading) }}
        />
        <Grid3>
          {d.eventDetails?.days?.map((x: any, i: number) => (
            <Card2 key={i} elevation={0}>
              <Row>
                <Badge>{x.badge}</Badge>
                <CardTitle
                  className="microsite-card-title"
                  dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(x.date) }}
                />
              </Row>
              <Html dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(x.body) ?? '' }} />
            </Card2>
          ))}
        </Grid3>
        <Box>
          <OutlineAction type="button" onClick={goLocations}>
            <span dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.eventDetails?.seeLocationDetails) }} />
          </OutlineAction>
        </Box>
      </SectionContent>
    </Section>
  );
}

function Agenda({
  scrollOffset
}: {
  scrollOffset: number;
}) {
  const { t } = useTranslation();
  const agenda = firstDrupalItem(t('microsite_agenda', {
    returnObjects: true,
    defaultValue: []
  }));
  const d = {
    heading: agenda.heading,
    days: agenda.days?.map((day: any) => ({
      id: day.id,
      badge: day.accordianBadge,
      label: day.accordianLabel,
      items: day.items
        ?.filter((item: any) => item.time || item.title || item.description)
        .map((item: any) => ({
          id: item.id,
          time: item.time,
          html: `${item.title ?? ''}${item.description ?? ''}`
        }))
    }))
  };
  const [open, setOpen] = useState<Array<number>>([0]);
  return (
    <Section
      scrollOffset={scrollOffset}
      id="agenda"
      shaded
      aria-labelledby="agenda-title"
    >
      <SectionContent>
        <AnchorHeading
          id="agenda"
          label={d.heading}
        />
        <AccordionList>
          {d.days?.map((day: any, i: number) => (
            <CustomAccordion
              key={day.id ?? i}
              label={
                day.accessibilityLabel ??
                [day.badge, day.label].filter(Boolean).join(': ')
              }
              open={open.includes(i)}
              onChange={(v) => setOpen(prev => prev.includes(i) ? prev.filter(v => v !== i) : [...prev, i])}
              summary={
                <AccordionRow>
                  <Badge>{day.badge}</Badge>
                  <SmallTitle component="span" dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(day.label) }} />
                </AccordionRow>
              }
            >
              {typeof day.items === 'string' ? (
                <AgendaItemHtml
                  dangerouslySetInnerHTML={{
                    __html: sanitizeDrupalHtml(day.items)
                  }}
                />
              ) : (
                <AgendaItems>
                  {day.items?.map((item: any, index: number) => (
                    <AgendaItemRow key={item.id ?? index}>
                      <AgendaTime>{item.time}</AgendaTime>

                      <AgendaItemContent>
                        <AgendaItemHtml
                          dangerouslySetInnerHTML={{
                            __html: sanitizeDrupalHtml(item.html ?? item.body ?? '')
                          }}
                        />
                      </AgendaItemContent>
                    </AgendaItemRow>
                  ))}
                </AgendaItems>
              )}
            </CustomAccordion>
          ))}
        </AccordionList>
      </SectionContent>
    </Section>
  );
}

function Locations({
  scrollOffset
}: {
  scrollOffset: number;
}) {
  const { t } = useTranslation();
  const locations = firstDrupalItem(t('microsite_locations', {
    returnObjects: true,
    defaultValue: []
  }));
  const shuttle = firstDrupalItem(t('microsite_shuttle', {
    returnObjects: true,
    defaultValue: []
  }));
  const d = {
    heading: locations.heading,
    intro: locations.intro,
    venues: locations.venue?.map((venue: any) => {
      const directions = venue.getDirections?.[0] ?? {};
      return {
        id: venue.id,
        name: venue.name,
        address: venue.address,
        info: venue.info,
        image: {
          alt: venue.image?.alt,
          value: getFullImageLink(venue.image?.url)
        },
        directions: {
          label: directions.label,
          info: venue.info,
          url: directions.url?.uri,
          newWindow: directions.newWindow === true || directions.newWindow === '1'
        }
      };
    }),
    shuttle: {
      heading: shuttle.title,
      intro: shuttle.description,
      image: {
        alt: shuttle.image?.alt,
        value: getFullImageLink(shuttle.image?.url)
      },
      days: shuttle.days?.map((day: any) => ({
        id: day.id,
        date: day.date,
        trips: day.trips?.map(
          (trip: any) =>
            `<p><strong>${trip.time ?? ''}</strong> &nbsp;&nbsp; ${trip.description}</p>`
        )
      })),
      note: shuttle.note
    }
  };
  return (
    <Section
      scrollOffset={scrollOffset}
      id="locations"
      aria-labelledby="locations-title"
    >
      <SectionContent>
        <AnchorHeading
          id="locations"
          label={d.heading}
        />
        <Text
          component="div"
          dangerouslySetInnerHTML={{
            __html: sanitizeDrupalHtml(d.intro)
          }}
        />
        <VenueGrid>
          {d.venues?.map((v: any, i: number) => (
            <VenueCard key={v.id ?? i}>
              <VenueImageFrame>
                <CoverImage src={v.image?.value} alt={v.image?.alt || v.name || ''} />
                <VenueOverlay>
                  <OverlayTitle>{v.name}</OverlayTitle>
                  <OverlayAddress
                    component="div"
                    dangerouslySetInnerHTML={{__html: sanitizeDrupalHtml(v.address ?? '')}}
                  />
                </VenueOverlay>
              </VenueImageFrame>
              <Text component="div" dangerouslySetInnerHTML={{__html: sanitizeDrupalHtml(v.directions.info)}} />
              <Directions
                href={v.directions?.url}
                target={v.directions?.newWindow ? '_blank' : undefined}
                rel={
                  v.directions?.newWindow ? 'noopener noreferrer' : undefined
                }
              >
                <span dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(v.directions?.label) }} />{' '}
                <VisuallyHidden>
                  {t('accessibilityMicrosite.directionsTo', {
                    name: v.name,
                  })}
                  {v.directions?.newWindow
                    ? ` (${t('accessibilityMicrosite.opensInNewTab', {
                      })})`
                    : ''}
                </VisuallyHidden>{' '}
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M10.6667 10.6667H1.33333V1.33333H6V0H1.33333C0.593333 0 0 0.6 0 1.33333V10.6667C0 11.4 0.593333 12 1.33333 12H10.6667C11.4 12 12 11.4 12 10.6667V6H10.6667V10.6667ZM7.33333 0V1.33333H9.72667L3.17333 7.88667L4.11333 8.82667L10.6667 2.27333V4.66667H12V0H7.33333Z"
                    fill="#1460AA"
                  />
                </svg>
              </Directions>
            </VenueCard>
          ))}
        </VenueGrid>
        <ShuttleGrid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}
          >
            <SmallTitle
              component="div"
              sx={{flex: 'unset'}}
              dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.shuttle?.heading) }}
            />
            <Text 
              component="div"
              dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.shuttle?.intro) ?? '' }}
            />
            {d.shuttle?.days?.map((day: any, i: number) => (
              <Box key={day.id ?? i} mt={2}>
                <SmallTextH4 component="div" dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(day.date) }} />
                {day.trips?.map((html: string, j: number) => (
                  <Html key={j} dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(html) }} />
                ))}
              </Box>
            ))}
            <Html dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.shuttle?.note) ?? '' }} />
          </Box>
          <ShuttleImage>
            <CoverImage
              src={d.shuttle?.image?.value}
              alt={
                d.shuttle?.image?.alt ||
                t('accessibilityMicrosite.shuttleImageAlt')
              }
            />
          </ShuttleImage>
        </ShuttleGrid>
      </SectionContent>
    </Section>
  );
}

function Participants({
  scrollOffset
}: {
  scrollOffset: number;
}) {
  const { t } = useTranslation();
  const participants = firstDrupalItem(t('microsite_participants', {
    returnObjects: true,
    defaultValue: []
  }));
  const d = {
    heading: participants.heading,
    lastUpdated: participants.lastUpdated,
    categories: participants.organization?.map((organization: any) => ({
      id: organization.id,
      name: organization.organizationType,
      logos:
        typeof organization.logos === 'object' &&
        !Array.isArray(organization.logos) &&
        organization.logos !== null
          ? [
              {
                value: getFullImageLink(organization.logos?.url),
                title: organization.logos?.title,
                alt: organization.logos?.alt,
                id: organization.logos?.id
              }
            ]
          : Array.isArray(organization.logos)
            ? organization?.logos?.map((logo: any) => ({
                id: logo.id,
                alt: logo.alt,
                title: logo.title,
                value: getFullImageLink(logo.url)
              }))
            : []
    }))
  };
  const [open, setOpen] = useState<Array<number>>([0]);
  return (
    <Section
      scrollOffset={scrollOffset}
      id="participants"
      shaded
      aria-labelledby="participants-title"
    >
      <SectionContent>
        <AnchorHeading
          id="participants"
          label={d.heading}
        />
        <SmallText dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(d.lastUpdated) }} />
        <AccordionList>
          {d.categories?.map((cat: any, i: number) => {
            const logos = cat.logos ?? cat.items ?? [];
            const categoryName = cat.name;
            return (
              <CustomAccordion
                key={cat.id ?? i}
                label={cat.accessibilityLabel ?? categoryName}
                open={open.includes(i)}
                onChange={(v) => setOpen(prev => prev.includes(i) ? prev.filter(idx => idx !== i) : [...prev, i])}
                summary={
                  <SmallTitle
                    component="span"
                    dangerouslySetInnerHTML={{ __html: sanitizeDrupalHtml(cat.name) }}
                  />
                }
              >
                {logos.length ? (
                  (() => {
                   const named = logos.map((logo: any, j: number) => ({
                      key: logo.id ?? j,
                      src: logo.image?.value ?? logo.value,
                      name: resolveLogoName(
                        logo,
                        t('accessibilityMicrosite.partnerLogoAlt')
                      )
                    }));
                    return (
                      <>
                        <LogoGrid component="ul" role="list" aria-hidden="true">
                          {named.map((logo) => (
                            <LogoItem key={logo.key}>
                              <Logo src={logo.src} alt={logo.name} />
                            </LogoItem>
                          ))}
                        </LogoGrid>
                        <Box
                          component="ul"
                          sx={visuallyHidden}
                          aria-label={cat.accessibilityLabel ?? categoryName}
                        >
                          {named.map((logo) => (
                            <li key={logo.key}>{logo.name}</li>
                          ))}
                        </Box>
                      </>
                    );
                  })()
                ) : (
                  <SmallText>{t('accessibilityMicrosite.comingSoon')}</SmallText>
                )}
              </CustomAccordion>
            );
          })}
        </AccordionList>
      </SectionContent>
    </Section>
  );
}

export default function MicroSiteContent({
  logoHeight
}: {
  logoHeight: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [active, setActive] = useState<SectionId>('overview');
  const activeRef = useRef<SectionId>(active);
  activeRef.current = active;
  const anchorRef = useRef<{
    id: SectionId;
    offset: number;
    atBottom: boolean;
  } | null>(null);

  const hero = firstDrupalItem(t('microsite_herosection', {
    returnObjects: true,
    defaultValue: []
  }));
  const overview = {
    register: {
      label: hero.registerUrl?.title,
      url: hero.registerUrl?.uri
    }
  };

  const navigating = useRef(false);
  const releaseTimer = useRef<number | null>(null);
  const settleRaf = useRef<number | null>(null);
  const releaseCleanup = useRef<(() => void) | null>(null);
  const initialNavigationComplete = useRef(false);
  const stickyNavRef = useRef<HTMLElement | null>(null);

  const cancelPendingRelease = useCallback(() => {
    if (releaseTimer.current !== null) {
      window.clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
    if (settleRaf.current !== null) {
      window.cancelAnimationFrame(settleRaf.current);
      settleRaf.current = null;
    }
    releaseCleanup.current?.();
    releaseCleanup.current = null;
  }, []);

  const releaseWhenScrollSettles = useCallback(
    (maxWaitMs: number) => {
      cancelPendingRelease();

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        navigating.current = false;
        cancelPendingRelease();
      };

      if ('onscrollend' in window) {
        const handleScrollEnd = () => finish();
        window.addEventListener('scrollend', handleScrollEnd, {
          once: true
        });
        releaseCleanup.current = () =>
          window.removeEventListener('scrollend', handleScrollEnd);
      } else {
        const STABLE_MS = 120;
        let lastY = window.scrollY;
        let lastChangeAt = performance.now();

        const tick = () => {
          const now = performance.now();
          const y = window.scrollY;

          if (y !== lastY) {
            lastY = y;
            lastChangeAt = now;
          }

          if (now - lastChangeAt >= STABLE_MS) {
            finish();
            return;
          }

          settleRaf.current = window.requestAnimationFrame(tick);
        };

        settleRaf.current = window.requestAnimationFrame(tick);
      }

      releaseTimer.current = window.setTimeout(finish, maxWaitMs);
    },
    [cancelPendingRelease]
  );

  const safeLogoHeight = Math.max(0, logoHeight);
  const whiteNavHeight = mobile ? NAV_MOBILE_HEIGHT : NAV_DESKTOP_HEIGHT;
  const sectionScrollOffset = safeLogoHeight + whiteNavHeight + SECTION_TOP_GAP;

  const getProbeY = useCallback(() => {
    const measured = stickyNavRef.current?.getBoundingClientRect().bottom;
    if (measured !== undefined) return measured + SECTION_TOP_GAP;

    const currentWhiteNavHeight = mobile
      ? NAV_MOBILE_HEIGHT
      : NAV_DESKTOP_HEIGHT;
    return Math.max(0, logoHeight) + currentWhiteNavHeight + SECTION_TOP_GAP;
  }, [logoHeight, mobile]);

  useEffect(() => cancelPendingRelease, [cancelPendingRelease]);

  const go = useCallback(
    (
      id: SectionId,
      mode: 'push' | 'replace' | 'none' = 'push',
      behavior: ScrollBehavior = 'smooth'
    ) => {
      const element = document.getElementById(id);
      if (!element) return;

      const totalOffset = getProbeY();

      navigating.current = true;
      setActive(id);
      anchorRef.current = { id, offset: 0, atBottom: false };

      if (mode === 'push') {
        window.history.pushState({ section: id }, '', `#${id}`);
      } else if (mode === 'replace') {
        window.history.replaceState({ section: id }, '', `#${id}`);
      }

      const targetPosition =
        element.getBoundingClientRect().top + window.scrollY - totalOffset;

      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior
      });

      // WCAG 2.4.3: when navigation is triggered explicitly (sticky nav click),
      // move keyboard focus to the destination section's heading so keyboard/AT
      // users land where sighted users land. Skipped for popstate/initial-load
      // syncs (mode !== 'push') so focus isn't stolen on page load or back/forward.
      if (mode === 'push') {
        const heading = document.getElementById(`${id}-title`);
        heading?.focus({ preventScroll: true });
      }

      if (behavior === 'smooth') {
        releaseWhenScrollSettles(1500);
      } else {
        cancelPendingRelease();
        releaseTimer.current = window.setTimeout(() => {
          navigating.current = false;
        }, 100);
      }
    },
    [getProbeY, releaseWhenScrollSettles, cancelPendingRelease]
  );

  useEffect(() => {
    let robots = document.head.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );

    const created = !robots;
    const old = robots?.content;

    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }

    robots.content = 'noindex, nofollow';

    return () => {
      if (created) robots?.remove();
      else if (robots && old !== undefined) robots.content = old;
    };
  }, []);

  useLayoutEffect(() => {
    if (initialNavigationComplete.current) return;

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    const rawHash = decodeURIComponent(
      window.location.hash.replace(/^#/, '')
    ) as SectionId;

    const initialSection: SectionId = IDS.includes(rawHash)
      ? rawHash
      : 'overview';

    navigating.current = true;
    setActive(initialSection);

    let cancelled = false;
    let rafOne = 0;
    let rafTwo = 0;
    let settleTimer = 0;

    const scrollToInitialSection = () => {
      if (cancelled) return;

      const element = document.getElementById(initialSection);
      if (!element) return;

      const totalOffset = getProbeY();

      const targetPosition =
        element.getBoundingClientRect().top + window.scrollY - totalOffset;

      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: 'auto'
      });
    };

    const finishInitialNavigation = () => {
      scrollToInitialSection();
      initialNavigationComplete.current = true;
      navigating.current = false;
    };

    rafOne = window.requestAnimationFrame(() => {
      rafTwo = window.requestAnimationFrame(() => {
        scrollToInitialSection();

        settleTimer = window.setTimeout(finishInitialNavigation, 250);
      });
    });

    const handleLoad = () => {
      if (!initialNavigationComplete.current) {
        finishInitialNavigation();
      }
    };

    window.addEventListener('load', handleLoad, { once: true });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafOne);
      window.cancelAnimationFrame(rafTwo);
      window.clearTimeout(settleTimer);
      window.removeEventListener('load', handleLoad);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [getProbeY]);

  useEffect(() => {
    const handleHistoryNavigation = () => {
      const hash = decodeURIComponent(
        window.location.hash.replace(/^#/, '')
      ) as SectionId;

      const section: SectionId = IDS.includes(hash) ? hash : 'overview';
      go(section, 'none', 'auto');
    };

    window.addEventListener('popstate', handleHistoryNavigation);
    window.addEventListener('hashchange', handleHistoryNavigation);

    return () => {
      window.removeEventListener('popstate', handleHistoryNavigation);
      window.removeEventListener('hashchange', handleHistoryNavigation);
    };
  }, [go]);

  useEffect(() => {
    let raf = 0;

    const update = () => {
      if (!initialNavigationComplete.current) return;
      if (navigating.current) return;

      const probeY = getProbeY();

      const sections = IDS.map((id) => document.getElementById(id)).filter(
        Boolean
      ) as HTMLElement[];

      if (sections.length === 0) return;

      const doc = document.documentElement;
      const viewportHeight = doc.clientHeight || window.innerHeight;
      const maxScrollY = Math.max(0, doc.scrollHeight - viewportHeight);
      const atMaxScroll =
        window.scrollY > 0 &&
        maxScrollY - window.scrollY <= AT_BOTTOM_TOLERANCE_PX;

      const sectionTops = sections.map(
        (section) => section.getBoundingClientRect().top
      );

      const index = pickStableSection(
        sectionTops,
        sections.map((section) => section.getBoundingClientRect().bottom),
        probeY,
        viewportHeight,
        sections.findIndex((section) => section.id === activeRef.current),
        atMaxScroll
      );
      const current = sections[index];
      if (!current) return;

      const id = current.id as SectionId;

      anchorRef.current = {
        id,
        offset: sectionTops[index] - probeY,
        atBottom: atMaxScroll
      };

      setActive((old) => (old === id ? old : id));

      if (window.location.hash !== `#${id}`) {
        window.history.replaceState({ section: id }, '', `#${id}`);
      }
    };

    const onScroll = () => {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(update);
    };

    let lastUserScrollAt = -Infinity;
    const markUserScroll = () => {
      lastUserScrollAt = performance.now();
    };
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) markUserScroll();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (USER_SCROLL_KEYS.has(e.key)) markUserScroll();
    };

    
    const reanchor = (snapToBottom: boolean, fromObserver = false) => {
      const anchor = anchorRef.current;
      if (!anchor || !initialNavigationComplete.current) return;
      if (navigating.current) return;
      if (
        fromObserver &&
        performance.now() - lastUserScrollAt < USER_SCROLL_IDLE_MS
      ) {
        return;
      }

      const target = document.getElementById(anchor.id);
      if (!target) return;

      const doc = document.documentElement;
      const maxScrollY = Math.max(
        0,
        doc.scrollHeight - (doc.clientHeight || window.innerHeight)
      );
      const nextY =
        snapToBottom && anchor.atBottom
          ? maxScrollY
          : window.scrollY +
            reanchorScrollDelta(
              anchor.offset,
              target.getBoundingClientRect().top,
              getProbeY()
            );
      const clamped = Math.min(maxScrollY, Math.max(0, nextY));
      if (Math.abs(clamped - window.scrollY) >= 1) {
        window.scrollTo({ top: clamped, behavior: 'auto' });
      }
    };

    const onResize = () => {
      reanchor(true);
      onScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchmove', markUserScroll, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => reanchor(false, true));
      observer.observe(document.body);
    }

    reanchor(false);

    return () => {
      window.cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchmove', markUserScroll);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [getProbeY]);

  useEffect(
    () => () => {
      if (releaseTimer.current !== null) {
        window.clearTimeout(releaseTimer.current);
      }
    },
    []
  );

  return (
    <ThemeProvider theme={microSiteTheme}>
      <SkipToContentLink
        href="#microsite-main-content"
        onClick={(e) => {
         const main = document.getElementById('microsite-main-content');
          if (!main) return;
          e.preventDefault();
          main.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const focusTarget =
            main.querySelector<HTMLElement>('h1, h2, h3, [tabindex="-1"]') ??
            main;
          focusTarget.focus({ preventScroll: true });
        }}
      >
        {t('accessibilityMicrosite.skipToContent')}
      </SkipToContentLink>

      <Box>
        <SectionNav
          navRef={stickyNavRef}
          logoHeight={safeLogoHeight}
          active={active}
          go={(id) => go(id)}
          registration={overview.register}
        />

        <Box id="microsite-main-content" component="main" tabIndex={-1}>
          <Overview scrollOffset={sectionScrollOffset} />

          <About
            goLocations={() => go('locations')}
            scrollOffset={sectionScrollOffset}
          />

          <Agenda scrollOffset={sectionScrollOffset} />
          <Locations scrollOffset={sectionScrollOffset} />
          <Participants scrollOffset={sectionScrollOffset} />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
